import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './CredentialDetailsModal.css';

const CredentialDetailsModal = ({ 
  isOpen, 
  onClose, 
  credential,
  programName,
  school = 'UIUC',
  onDeleteCredential
}) => {
  const [activeTab, setActiveTab] = useState('certification');
  
  if (!isOpen || !credential) return null;

  // Mock data for certificate details - in a real app this would come from props or API
  const certificateDetails = {
    studentName: `2025 Cohort of the ${programName}`,
    issueDate: "May 29, 2025",
    certificateId: "0394829",
    founder: "Tony Zhang, Founder, AristAI, Inc.",
    courseCodes: credential.courseCodes || []
  };

  const handleDeleteCredential = () => {
    if (onDeleteCredential) {
      onDeleteCredential(credential.name);
      onClose(); // Close the modal after deletion
    }
  };

  const handleDownloadPDF = async () => {
    try {
      // Get the certificate preview element
      const certificateElement = document.querySelector('.certificate-preview');
      if (!certificateElement) {
        console.error('Certificate element not found');
        return;
      }

      // Convert the certificate to canvas
      const canvas = await html2canvas(certificateElement, {
        scale: 4, // Much higher quality (increased from 2 to 4)
        useCORS: true, // Allow cross-origin images
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 15000, // Longer timeout for high-res rendering
        removeContainer: true,
        width: certificateElement.offsetWidth,
        height: certificateElement.offsetHeight,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0,
        foreignObjectRendering: false // Better SVG rendering
      });

      // Get canvas dimensions
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate PDF dimensions for high quality
      const pdfWidth = 297; // A4 width in mm (landscape)
      const pdfHeight = (imgHeight * pdfWidth) / imgWidth;
      
      // Create PDF with high quality settings
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [pdfWidth, pdfHeight],
        compress: false // Disable compression for better quality
      });
      
      // Convert to high-quality image data
      const imgData = canvas.toDataURL('image/jpeg', 1.0); // Max quality JPEG
      
      // Add image to PDF with high quality
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, '', 'FAST');
      
      // Generate filename
      const filename = `${credential.name.replace(/\s+/g, '_')}_Certificate.pdf`;
      
      // Download the PDF
      pdf.save(filename);
      
      console.log('PDF downloaded successfully:', filename);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div className="credential-details-overlay" onClick={onClose}>
      <div className="credential-details-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="close-button" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Modal Header */}
        <div className="modal-header">
          <div className="credential-title-section">
            <h1 className="credential-title">{credential.name}</h1>
            <p className="credential-subtitle">Analytics Certification</p>
          </div>
        </div>

        {/* Section Header */}
        <div className="section-header">
          <div className="section-header-content">
            <div className="section-title-row">
              <button 
                className={`section-tab ${activeTab === 'certification' ? 'active' : ''}`}
                onClick={() => setActiveTab('certification')}
              >
                Certification
              </button>
              <button 
                className={`section-tab ${activeTab === 'courses' ? 'active' : ''}`}
                onClick={() => setActiveTab('courses')}
              >
                Required Courses ({credential.courseCount})
              </button>
            </div>
            <div className="section-divider"></div>
          </div>
        </div>

        {/* Certificate Preview - Only show when certification tab is active */}
        {activeTab === 'certification' && (
          <div className="certificate-container">
            <div className="certificate-preview">
              {/* Badge */}
              <div className="certificate-badge">
                <div className="badge-background">
                  <img 
                    src={credential.badgeImage} 
                    alt={`${credential.name} Badge`}
                    className="badge-image-preview"
                  />
                </div>
              </div>

              {/* Certificate Content */}
              <div className="certificate-content">
                <div className="certificate-header">PROFESSIONAL CERTIFICATION</div>
                <div className="certificate-name">{credential.name}</div>
                <div className="issued-to-label">ISSUED TO</div>
                <div className="student-name">{certificateDetails.studentName}</div>
              </div>

              {/* Certificate Footer */}
              <div className="certificate-footer">
                <div className="founder-signature">
                  <img src="/signature.png" alt="Founder Signature" className="signature-image" />
                  <div className="founder-name">{certificateDetails.founder}</div>
                </div>
                
                <div className="certificate-details">
                  <div className="issue-date">Issued on: {certificateDetails.issueDate}</div>
                  <div className="certificate-id">ID: {certificateDetails.certificateId}</div>
                </div>
              </div>

              {/* AristAI Logos */}
              <div className="aristai-logo">
                <img src="/AristAI-logo-2.svg" alt="AristAI Icon" className="aristai-icon" />
                <img src="/AristAI-logo-text.svg" alt="AristAI Text" className="aristai-text" />
              </div>

              {/* Barcode */}
              <div className="qr-code">
                <img src="/barcode.png" alt="Certificate Barcode" className="barcode-image" />
              </div>
            </div>
          </div>
        )}

        {/* Required Courses Content - Only show when courses tab is active */}
        {activeTab === 'courses' && (
          <div className="required-courses-main-container">
            <div className="required-courses-grid-container">
              {certificateDetails.courseCodes && certificateDetails.courseCodes.length > 0 ? (
                certificateDetails.courseCodes.map((courseCode, index) => (
                  <div key={index} className="required-course-card">
                    <span className="required-course-title">{courseCode}</span>
                    <div className="required-course-check-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#047857"/>
                        <path d="m9 12 2 2 4-4" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-courses-message">
                  <span>No required courses available</span>
                </div>
              )}
            </div>

            {/* Pagination for Required Courses */}
            <div className="required-courses-pagination-container">
              <button className="required-courses-pagination-arrow required-courses-pagination-arrow-left">
                <svg width="12" height="24" viewBox="0 0 12 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 2L2 12L10 22" stroke="#BBBBBB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <span className="required-courses-pagination-text">Page 1 of 1</span>
              <button className="required-courses-pagination-arrow required-courses-pagination-arrow-right">
                <svg width="12" height="24" viewBox="0 0 12 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 2L10 12L2 22" stroke="#C3C3C3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons - Only show when certification tab is active */}
        {activeTab === 'certification' && (
          <>
            {/* Download PDF Button */}
            <button className="download-button" onClick={handleDownloadPDF}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M7 10L12 15M12 15L17 10M12 15V3" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Download PDF</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CredentialDetailsModal;



